import type { FirebaseApp } from 'firebase/app';
import {
  type DocumentData,
  type Firestore,
  type WriteBatch,
  arrayRemove,
  arrayUnion,
  collection,
  deleteField,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import {
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable,
  type Functions,
} from 'firebase/functions';
import type { EmulatorEndpoint } from '@taki/runtime-config';
import {
  RecurrenceSchema,
  RecurrenceCreateInputSchema,
  RecurrenceUpdateInputSchema,
  estFamille,
  messageFamilleInconnue,
  schemaDeFamille,
  titreSansPeriode,
  type Recurrence,
  type RecurrenceCreateInput,
  type RecurrenceUpdateInput,
  type Template,
  type TemplateCreateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import {
  type CountOptions,
  type ListOptions,
  runAverage,
  runCount,
  runList,
  runSum,
} from './list-query.js';
import { createArchiveOps } from './archive-ops.js';
import {
  type TemplateChildFields,
  type TemplateChildFromRecordInput,
  type TemplateRepository,
  TEMPLATES_COLLECTION,
  poserParChemin,
  sansValeursVides,
  templateChildFromRecord,
  templateFieldsFromRecord,
} from './templates.js';

/**
 * Repository de la famille `recurrence` (plan recurrent, org-wide).
 *
 * Replique le PATRON des modeles (`templates.ts`) pour les huit verbes du
 * socle, et en ajoute trois :
 *   - `fromRecord` : tire d'une fiche EXISTANTE un modele, puis le plan qui le
 *     cite (« Créer un plan récurrent ») ;
 *   - `fromTemplate` : tire un plan d'un modele existant ;
 *   - `lancer` : demande au serveur un passage IMMEDIAT, sans attendre celui
 *     de la nuit.
 *
 * UN PLAN = UN RYTHME + UN MODELE (`templateId`). Le contenu des fiches vit
 * dans le modele, jamais dans le plan : on corrige le prix du service dans le
 * modele (mode Modeles), et les occurrences suivantes naissent au bon prix. Le
 * modele tient la liste des plans qui le citent (`plansIds`), ecrite ICI, dans
 * le meme lot que le plan : c'est ce qui le protege d'un retrait.
 *
 * CE FICHIER NE CALCULE AUCUNE OCCURRENCE, et ce n'est pas un oubli. Le moteur
 * vit dans `functions/src/recurrence/moteur.ts`, et nulle part ailleurs : deux
 * moteurs, un dans le navigateur et un sur le serveur, finiraient par ne plus
 * tomber sur le meme jour, et une fiche naitrait deux fois. Le client ECRIT un
 * plan, le LIT, et demande un passage.
 *
 * CE QUE LE SERVEUR NE VERIFIE PAS, ET QUI SE VERIFIE ICI. `functions/` ne peut
 * importer aucun `@taki/*` (docs/FUNCTIONS.md), donc aucun schema Zod : il
 * controle la FORME d'un plan, pas que la fiche produite sera valide pour sa
 * famille. C'est `validerPlanContreFamille` qui le fait, AVANT toute
 * ecriture d'un plan. Risque accepte : un plan ecrit hors du SDK (console,
 * script) peut produire des fiches que le SDK refuserait ensuite de lire.
 */

export type { RecurrenceCreateInput };

/** Nom de la collection Firestore de la famille recurrence. */
export const RECURRENCES_COLLECTION = 'recurrences';

/**
 * L'AUTEUR des ecritures du passage quotidien (`createdBy`/`updatedBy` des
 * fiches creees et du plan). Un client ne peut JAMAIS l'ecrire : les rules
 * exigent `createdBy == request.auth.uid`. Recopie de
 * `functions/src/recurrence/contrat.ts`.
 */
export const RECURRENCE_SERVER_ACTOR = 'system:recurrence';

/**
 * Region des Cloud Functions. DOIT valoir celle de `functions/src/runtime.ts`.
 * Viser la mauvaise region fait echouer l'appel sur un « Failed to fetch ».
 */
export const RECURRENCES_FUNCTIONS_REGION = 'europe-west1';

/** Le nom de la callable du passage immediat. */
export const CALLABLE_RECURRENCES = 'lancerRecurrences';

/**
 * LES DEUX CLEFS QUI RATTACHENT UNE FICHE A SON PLAN, posees par le serveur
 * dans le sac de la fiche RACINE creee : `customFields.recurrenceId` (l'id du
 * plan) et `customFields.occurrence` (le jour `AAAA-MM-JJ` qu'elle sert).
 */
export const CLEF_LIEN_PLAN = 'recurrenceId';
export const CLEF_OCCURRENCE = 'occurrence';

/** Le prefixe du sac, meme sens que dans `templates.ts`. */
const PREFIXE_SAC = 'customFields.';

/** Un objet nu (le sac `customFields` en est un). */
function estUnSac(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Le jour LOCAL d'une date, `AAAA-MM-JJ`. Local et non UTC : c'est le jour que voit la personne devant l'ecran. */
export function jourLocal(date: Date): string {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const jj = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${mm}-${jj}`;
}

/** Lit la valeur au CHEMIN DE STOCKAGE `chemin` (racine, ou `customFields.x`). */
function lireParChemin(record: Record<string, unknown>, chemin: string): unknown {
  if (!chemin.startsWith(PREFIXE_SAC)) return record[chemin];
  const sac = record['customFields'];
  return estUnSac(sac) ? sac[chemin.slice(PREFIXE_SAC.length)] : undefined;
}

/**
 * LE CONTENU D'UNE FICHE, TEL QUE LE MODELE D'UN PLAN LE RETIENT. Fonction
 * PURE : ce sont les `fields` du modele que « Créer un plan récurrent » ecrit
 * (`modeleDepuisFiche`).
 *
 * Le tri des modeles d'abord (`templateFieldsFromRecord` : champs standard et
 * `type` retires), puis trois retraits de plus, propres a la recurrence :
 *   - LES DATES ABSOLUES de premier niveau (`startAt`, `dueAt`) : recopiees,
 *     elles seraient LA MEME sur chaque occurrence, donc fausses des la
 *     deuxieme. Les dates d'une occurrence se posent par les chemins nommes du
 *     plan (`champDateOccurrence`, `champDateEcheance`), calcules par le
 *     serveur ;
 *   - LE RATTACHEMENT a un plan (`customFields.recurrenceId`, `occurrence`) :
 *     une fiche produite par un plan qu'on rend recurrente a son tour ne doit
 *     pas faire naitre des fiches rattachees a l'ANCIEN plan ;
 *   - les chaines vides de premier niveau (`sansValeursVides`), pour la raison
 *     des modeles : `''` n'est ecrivable nulle part sur ces champs, et le
 *     serveur ne passe par aucun Zod pour le retirer.
 */
export function champsDuPlan(record: Record<string, unknown>, cibleType: string): Record<string, unknown> {
  const { fields } = templateFieldsFromRecord({ ...record, type: cibleType });
  const garde: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(fields)) {
    if (value instanceof Date) continue;
    garde[key] = value;
  }
  const sac = garde['customFields'];
  if (estUnSac(sac)) {
    const { [CLEF_LIEN_PLAN]: _plan, [CLEF_OCCURRENCE]: _occurrence, ...reste } = sac;
    garde['customFields'] = reste;
  }
  return sansValeursVides(garde);
}

/**
 * Un enfant retenu par le modele d'un plan : le tri des modeles, SANS SES DATES ABSOLUES
 * non plus, pour la meme raison que la racine (une date recopiee est la meme
 * sur chaque occurrence). Les chaines vides partent aussi.
 */
function enfantDuPlan(input: TemplateChildFromRecordInput): TemplateChildFields {
  const enfant = templateChildFromRecord(input);
  const fields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(enfant.fields)) {
    if (value instanceof Date) continue;
    fields[key] = value;
  }
  return { ...enfant, fields: sansValeursVides(fields) };
}

/** Ce qu'il faut pour tirer un plan d'une fiche existante (« Créer un plan récurrent »). */
export interface RecurrenceFromRecordInput {
  /** Clef du repository de la fiche dans `TakiSdk` (ex. `projects`) : le `type` du plan et du modele. */
  family: string;
  /**
   * Le sous-type de la porte. Le `type` porte par `record` GAGNE s'il est une
   * chaine non vide, comme pour un enfant de modele.
   */
  type?: string | undefined;
  /** La fiche telle qu'elle a ete lue (champs standard compris : ils sont retires ici). */
  record: Record<string, unknown>;
  /** Le nom du plan, saisi par la personne. C'est aussi le nom du modele : une seule saisie. */
  name: string;
  description?: string | undefined;
  /** Les fiches liees a recreer a chaque occurrence, telles que l'ecran les a lues. */
  children?: readonly TemplateChildFromRecordInput[] | undefined;
}

/** Ce qu'il faut pour tirer un plan d'un modele existant (« Créer un plan récurrent » en mode Modeles). */
export interface RecurrenceFromTemplateInput {
  /** L'id du modele cite par le plan. */
  templateId: string;
  /** Le nom du plan, saisi par la personne. */
  name: string;
  description?: string | undefined;
}

/** Ce qu'un plan lit d'un modele pour naitre : son id, sa cible et son contenu. */
export type ModeleDuPlan = Pick<Template, 'id' | 'type' | 'targetType' | 'name' | 'fields'>;

/** Le sous-type des fiches a creer : celui de la fiche, sinon celui de la porte. Leve sans aucun des deux. */
function cibleDeLaFiche(input: RecurrenceFromRecordInput): string {
  const typeDeLaFiche = input.record['type'];
  const cibleType =
    typeof typeDeLaFiche === 'string' && typeDeLaFiche.length > 0 ? typeDeLaFiche : input.type;
  if (typeof cibleType !== 'string' || cibleType.length === 0) {
    throw new Error(
      "Plan recurrent : la fiche n'a de `type` nulle part (ni sur la fiche, ni sur la porte). " +
        "Les fiches creees par ce plan naitraient sans sous-type, et aucune porte ne les montrerait.",
    );
  }
  return cibleType;
}

/**
 * LE MODELE QU'UNE FICHE DONNE A SON PLAN. Fonction PURE.
 *
 * « Créer un plan récurrent » depuis une fiche ecrit DEUX documents : ce
 * modele, puis le plan qui le cite. Le contenu est celui de `champsDuPlan`
 * (sans dates absolues ni rattachement a un ancien plan), les LIENS GARDES :
 * un service mensuel reste le service du MEME client (`contactId`). Le nom du
 * modele est celui du plan, la personne ne saisit qu'une fois.
 */
export function modeleDepuisFiche(orgId: string, input: RecurrenceFromRecordInput): TemplateCreateInput {
  const cibleType = cibleDeLaFiche(input);
  const modele: TemplateCreateInput = {
    orgId,
    type: input.family,
    targetType: cibleType,
    name: input.name,
    fields: champsDuPlan(input.record, cibleType),
  };
  if (input.children !== undefined) modele.children = input.children.map((child) => enfantDuPlan(child));
  const id = input.record['id'];
  if (typeof id === 'string' && id.length > 0) modele.sourceId = id;
  return modele;
}

/**
 * TIRE UN PLAN D'UN MODELE. Fonction PURE : `aujourdhui` est injecte, le test
 * fixe le jour.
 *
 * LE PLAN NAIT `suspendu`, et c'est la decision qui compte ici : il vient d'un
 * geste en un clic, avec des valeurs PAR DEFAUT (tous les mois, a partir
 * d'aujourd'hui). Actif d'emblee, le passage de la nuit creerait une fiche sur
 * un rythme que personne n'a encore relu. La personne ouvre le plan, regle le
 * rythme, puis le passe `actif`.
 *
 * LES DEFAUTS, un par un :
 *   - au calendrier, tous les mois, a partir d'aujourd'hui (jour LOCAL) ;
 *   - le jour du mois d'aujourd'hui, PLAFONNE A 28 : un plan tire un 31 ne doit
 *     pas surprendre en tombant le 30 ou le 28 selon le mois ;
 *   - le libelle va dans `name` si le modele en porte un, sinon dans
 *     `customFields.libelle` s'il en porte un, sinon nulle part ;
 *   - le libelle des fiches a venir est ce titre SANS SA PERIODE, suivi des
 *     jetons du mois (le nom du modele quand le titre est vide). Sans ca, chaque
 *     occurrence reprendrait mot pour mot le titre du mois source
 *     (« ..., septembre 2026 ») : douze services homonymes par an.
 */
export function planDepuisModele(
  orgId: string,
  modele: ModeleDuPlan,
  name: string,
  aujourdhui: Date = new Date(),
): RecurrenceCreateInput {
  const plan: RecurrenceCreateInput = {
    orgId,
    type: modele.type,
    cibleType: modele.targetType,
    name,
    status: 'suspendu',
    mode: 'calendrier',
    rythmeUnite: 'mois',
    rythmeIntervalle: 1,
    jourDuMois: Math.min(aujourdhui.getDate(), 28),
    debut: jourLocal(aujourdhui),
    avanceJours: 0,
    sauts: [],
    templateId: modele.id,
    modeleLibelle: '',
  };

  const nom = modele.fields['name'];
  if (typeof nom === 'string' && nom.trim() !== '') plan.champLibelle = 'name';
  else if (lireParChemin(modele.fields, 'customFields.libelle') !== undefined) {
    plan.champLibelle = 'customFields.libelle';
  }

  if (plan.champLibelle !== undefined) {
    const lu = lireParChemin(modele.fields, plan.champLibelle);
    const titre = typeof lu === 'string' && lu.trim() !== '' ? lu : modele.name;
    plan.modeleLibelle = `${titreSansPeriode(titre)}, {mois} {annee}`;
  }
  return plan;
}

/**
 * TIRE UN PLAN D'UNE FICHE, une fois son modele ecrit (`modeleDepuisFiche`).
 * Fonction PURE : `planDepuisModele`, plus ce que la fiche sait et que le
 * modele ne garde pas, ses DATES.
 *   - une fiche qui avait un `startAt` le recoit a chaque occurrence, et le
 *     rythme suit la fiche : un service du 15 donne un plan du 15 ;
 *   - un `dueAt` aussi, avec l'ecart de la fiche d'origine en jours entiers.
 */
export function planDepuisFiche(
  orgId: string,
  input: RecurrenceFromRecordInput,
  modeleId: string,
  aujourdhui: Date = new Date(),
): RecurrenceCreateInput {
  const modele = modeleDepuisFiche(orgId, input);
  const plan = planDepuisModele(
    orgId,
    { id: modeleId, type: modele.type, targetType: modele.targetType, name: modele.name, fields: modele.fields },
    input.name,
    aujourdhui,
  );

  if (input.description !== undefined) plan.description = input.description;
  if (modele.sourceId !== undefined) plan.sourceId = modele.sourceId;

  const record = input.record;
  const debut = record['startAt'];
  const echeance = record['dueAt'];
  if (debut instanceof Date) {
    plan.champDateOccurrence = 'startAt';
    /* LE RYTHME SUIT LA FICHE, PAS LE JOUR DU CLIC : un service du 15 donne un
       plan du 15. Et le plan DEMARRE APRES elle, jamais avant : la fiche source
       couvre deja sa periode, la recreer ferait un doublon le premier matin. */
    plan.jourDuMois = Math.min(debut.getDate(), 28);
    const lendemain = new Date(debut.getFullYear(), debut.getMonth(), debut.getDate() + 1, 12);
    if (jourLocal(lendemain) > plan.debut) plan.debut = jourLocal(lendemain);
  }
  if (echeance instanceof Date) {
    plan.champDateEcheance = 'dueAt';
    if (debut instanceof Date) {
      const jours = Math.round((echeance.getTime() - debut.getTime()) / 86_400_000);
      plan.delaiEcheanceJours = Math.max(0, jours);
    }
  }

  return plan;
}

/**
 * CE QUE LA VALIDATION LIT D'UN PLAN : sa cible, son nom et ses chemins. Le
 * contenu, lui, vient du modele (`ContenuDuModele`).
 */
export interface PayloadDePlan {
  type: string;
  cibleType: string;
  name: string;
  champLibelle?: string | undefined;
  champDateOccurrence?: string | undefined;
  champDateEcheance?: string | undefined;
}

/** Ce que la validation lit d'un modele : le contenu de chaque fiche produite. */
export interface ContenuDuModele {
  fields: Record<string, unknown>;
  children?: ReadonlyArray<{ family: string; type: string; linkKey: string; fields: Record<string, unknown> }> | undefined;
}

/** Le verdict : le plan produira des fiches valides, ou la raison du contraire, en francais. */
export type VerdictDePlan = { ok: true } | { ok: false; raison: string };

/** Ce que `raisonDuModele` lit d'un modele lu : sa cible et son etat. */
export type EtatDuModele = Pick<Template, 'orgId' | 'type' | 'targetType' | 'archivedAt' | 'deletedAt'>;

/**
 * UN PLAN PEUT-IL CITER CE MODELE ? La raison du refus en francais, ou `null`.
 * Fonction PURE, exportee pour le test.
 *
 * Absent, supprime ou archive : le serveur suspendrait le plan au premier
 * passage, autant le dire devant la personne. Une autre org, une autre famille
 * ou un autre sous-type : les fiches produites ne seraient pas celles que le
 * plan annonce (les rules refusent aussi l'org et la famille).
 */
export function raisonDuModele(
  plan: Pick<PayloadDePlan, 'type' | 'cibleType'> & { orgId: string },
  modele: EtatDuModele | null,
): string | null {
  if (modele === null || modele.deletedAt != null) {
    return "Le modèle de ce plan est introuvable (supprimé) : rien n'a été écrit.";
  }
  if (modele.archivedAt != null) {
    return "Le modèle de ce plan est archivé : désarchivez-le d'abord. Rien n'a été écrit.";
  }
  if (modele.orgId !== plan.orgId) {
    return "Le modèle de ce plan appartient à une autre organisation : rien n'a été écrit.";
  }
  if (modele.type !== plan.type) {
    return (
      `Le modèle crée des fiches « ${modele.type} », le plan des fiches « ${plan.type} » : ` +
      "rien n'a été écrit."
    );
  }
  if (modele.targetType !== plan.cibleType) {
    return (
      `Le modèle crée des fiches de type « ${modele.targetType} », le plan des fiches « ${plan.cibleType} » : ` +
      "rien n'a été écrit."
    );
  }
  return null;
}

/**
 * DES CHAMPS STANDARD FACTICES, le temps d'un `safeParse` : ce qui se verifie
 * ici est le contenu METIER, les champs standard sont poses par le serveur.
 */
function champsStandardFactices(): Record<string, unknown> {
  const maintenant = new Date();
  return {
    id: 'validation',
    createdAt: maintenant,
    updatedAt: maintenant,
    createdBy: RECURRENCE_SERVER_ACTOR,
    updatedBy: RECURRENCE_SERVER_ACTOR,
    archivedAt: null,
    deletedAt: null,
    schemaVersion: CURRENT_SCHEMA_VERSION,
  };
}

/** La valeur que le serveur posera a un chemin de date : une date pure dans le sac, un instant a la racine. */
function dateFactice(chemin: string): unknown {
  return chemin.startsWith(PREFIXE_SAC) ? '2026-01-15' : new Date(Date.UTC(2026, 0, 15, 12));
}

/** Le premier refus de Zod, dit en francais, avec le chemin du champ fautif. */
function raisonDeZod(famille: string, issue: { path: ReadonlyArray<string | number>; code: string } | undefined): string {
  if (issue === undefined) return `Le plan ne produirait pas une fiche valide de « ${famille} ».`;
  const chemin = issue.path.join('.');
  return (
    `Le plan ne produirait pas une fiche valide de « ${famille} » : ` +
    `le champ « ${chemin} » manque ou n'a pas la forme attendue. Rien n'a ete ecrit.`
  );
}

/**
 * LA FICHE QUE LE PLAN PRODUIRA SERA-T-ELLE VALIDE POUR SA FAMILLE ? Fonction
 * PURE : elle simule ce que le serveur ecrira (le contenu DU MODELE, le
 * libelle, les dates, le rattachement), et le passe au schema de la famille
 * cible, enfants compris.
 *
 * POURQUOI ICI ET PAS SUR LE SERVEUR : le serveur n'a aucun schema Zod (il ne
 * peut importer aucun `@taki/*`). Sans ce controle, un plan invalide se
 * decouvrirait la nuit, dans un journal, au lieu de se refuser a l'ecriture,
 * devant la personne qui peut le corriger.
 *
 * CE QUI N'EST PAS COUVERT, et c'est assume : un modele CORRIGE apres coup
 * (mode Modeles) n'est pas revalide contre ses plans. Le serveur le relit a
 * chaque passage et journalise un echec.
 *
 * Les valeurs simulees sont FACTICES (un jour fixe, un id de plan fixe) : ce
 * qui se verifie est la FORME, pas la date.
 */
export function validerPlanContreFamille(
  plan: PayloadDePlan,
  modele: ContenuDuModele,
  orgId: string,
): VerdictDePlan {
  if (!estFamille(plan.type)) return { ok: false, raison: messageFamilleInconnue(plan.type) };

  let racine: Record<string, unknown> = {
    ...champsStandardFactices(),
    orgId,
    type: plan.cibleType,
    ...modele.fields,
  };
  if (plan.champLibelle) racine = poserParChemin(racine, plan.champLibelle, plan.name);
  if (plan.champDateOccurrence) {
    racine = poserParChemin(racine, plan.champDateOccurrence, dateFactice(plan.champDateOccurrence));
  }
  if (plan.champDateEcheance) {
    racine = poserParChemin(racine, plan.champDateEcheance, dateFactice(plan.champDateEcheance));
  }
  racine = poserParChemin(racine, `${PREFIXE_SAC}${CLEF_LIEN_PLAN}`, 'plan');
  racine = poserParChemin(racine, `${PREFIXE_SAC}${CLEF_OCCURRENCE}`, '2026-01-15');

  const verdict = schemaDeFamille(plan.type).safeParse(racine);
  if (!verdict.success) return { ok: false, raison: raisonDeZod(plan.type, verdict.error.issues[0]) };

  for (const enfant of modele.children ?? []) {
    if (!estFamille(enfant.family)) return { ok: false, raison: messageFamilleInconnue(enfant.family) };
    const donnees = poserParChemin(
      { ...champsStandardFactices(), orgId, type: enfant.type, ...enfant.fields },
      enfant.linkKey,
      'racine',
    );
    const verdictEnfant = schemaDeFamille(enfant.family).safeParse(donnees);
    if (!verdictEnfant.success) {
      return { ok: false, raison: raisonDeZod(enfant.family, verdictEnfant.error.issues[0]) };
    }
  }
  return { ok: true };
}

/**
 * LES CLEFS QU'UN PATCH PEUT EFFACER (`null`), et seulement elles. La fiche
 * generique ecrit `null` quand on vide une case facultative
 * (`validerChampNatif`), et `.optional()` refuse `null` : sans cette traduction
 * en `deleteField()`, vider la date de fin d'un plan echouerait. Les champs a
 * DEFAUT (`status`, `sauts`) n'y sont PAS : effaces, ils reliraient leur
 * defaut, et un plan suspendu redeviendrait `actif` en silence.
 */
const CLEFS_EFFACABLES: ReadonlySet<string> = new Set([
  'description',
  'sourceId',
  'joursSemaine',
  'jourDuMois',
  'fin',
  'realisationChamp',
  'realisationValeurs',
  'realisationDateChamp',
  'delaiNombre',
  'delaiUnite',
  'champLibelle',
  'modeleLibelle',
  'champDateOccurrence',
  'champDateEcheance',
  'delaiEcheanceJours',
]);

/** Les clefs dont un changement exige de revalider le plan contre son modele et la famille cible. */
const CLEFS_DU_CONTENU = [
  'cibleType',
  'name',
  'champLibelle',
  'champDateOccurrence',
  'champDateEcheance',
] as const;

/* -------------------------------------------------------------------------- */
/* Le passage immediat (callable `lancerRecurrences`)                          */
/* -------------------------------------------------------------------------- */

/** Ce que rend un passage demande a la main. */
export interface ResultatLancement {
  /** Le nombre de plans lus. */
  plans: number;
  /** Les fiches creees. */
  crees: number;
  /** Les occurrences deja servies (fiche deja la) : le curseur avance, rien n'est recree. */
  existants: number;
  /** Les occurrences sautees (`sauts`). */
  sautes: number;
  /** Les plans en echec, chacun avec son message. */
  echecs: Array<{ planId: string; message: string }>;
}

/** Ce qu'un echec de passage dit a l'ecran, en plus de son message. */
export class RecurrencesError extends Error {
  /** Le code de la callable (`permission-denied`, `not-found`...), ou `unknown`. */
  readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'RecurrencesError';
    this.code = code;
  }
}

/** Un entier lu dans une reponse, 0 sinon. */
function entier(valeur: unknown): number {
  return typeof valeur === 'number' && Number.isFinite(valeur) ? valeur : 0;
}

/** Lit la reponse de la callable sans lui faire confiance. Fonction PURE, exportee pour le test. */
export function lireResultatLancement(brut: unknown): ResultatLancement {
  const data = estUnSac(brut) ? brut : {};
  const echecs = Array.isArray(data['echecs']) ? data['echecs'] : [];
  return {
    plans: entier(data['plans']),
    crees: entier(data['crees']),
    existants: entier(data['existants']),
    sautes: entier(data['sautes']),
    echecs: echecs.filter(estUnSac).map((echec) => ({
      planId: String(echec['planId'] ?? ''),
      message: String(echec['message'] ?? ''),
    })),
  };
}

/**
 * Traduit l'echec de la callable en phrase d'ecran. Meme cas que la gestion
 * des comptes (`acces-comptes.ts`) : une Function PAS DEPLOYEE rend `not-found`
 * avec un message anglais qui ne dit rien a personne.
 */
function enErreur(cause: unknown): RecurrencesError {
  const objet = typeof cause === 'object' && cause !== null ? cause : {};
  const code = String(Reflect.get(objet, 'code') ?? 'unknown').replace(/^functions\//, '');
  const message = String(Reflect.get(objet, 'message') ?? cause);
  // Le `not-found` d'une Function absente porte le message generique du
  // transport ; celui du serveur (un plan d'une autre org) porte une phrase.
  const generique = message.trim() === '' || /^not[\s_-]?found$/i.test(message.trim());
  if (code === 'not-found' && generique) {
    return new RecurrencesError(
      "Le passage des plans n'est pas disponible dans cet environnement : " +
        'les Cloud Functions ne sont pas deployees.',
      code,
    );
  }
  if (code === 'internal' || code === 'unavailable') {
    return new RecurrencesError("Les Cloud Functions n'ont pas repondu. Rien n'a ete cree.", code);
  }
  if (code === 'permission-denied') {
    return new RecurrencesError("Votre acces ne permet pas de lancer les plans.", code);
  }
  return new RecurrencesError(message === '' ? 'Echec du passage.' : message, code);
}

/** Instances Functions deja branchees sur l'emulateur (cf. `createComptesClient`). */
const emulatorBoundFunctions = new WeakSet<Functions>();

/* -------------------------------------------------------------------------- */
/* Le repository                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Repository type pour la famille `recurrence`.
 * Toute lecture est validee par `RecurrenceSchema`, toute ecriture par
 * `RecurrenceCreateInputSchema`, puis contre son modele (`raisonDuModele`) et
 * la famille cible (`validerPlanContreFamille`).
 */
export interface RecurrenceRepository {
  /**
   * Ecrit le plan ET l'inscrit dans `plansIds` de son modele, dans UN lot :
   * les rules refusent l'un sans l'autre.
   */
  create(input: RecurrenceCreateInput): Promise<Recurrence>;
  update(id: string, patch: RecurrenceUpdateInput): Promise<Recurrence>;
  list(options?: ListOptions): Promise<Recurrence[]>;
  count(options?: CountOptions): Promise<number>;
  /** Somme serveur d'un champ, memes options que `count`. Cf. list-query.ts. */
  sum(field: string, options?: CountOptions): Promise<number>;
  /** Moyenne serveur d'un champ, `null` si aucun document. Cf. list-query.ts. */
  average(field: string, options?: CountOptions): Promise<number | null>;
  get(id: string): Promise<Recurrence | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. Ne libere PAS le modele. */
  archive(id: string): Promise<Recurrence>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Recurrence>;
  /**
   * Supprime (`deletedAt` + `archivedAt`), invisible cote client, et retire le
   * plan de `plansIds` de son modele dans le MEME lot. Cf. archive-ops.ts.
   */
  remove(id: string): Promise<Recurrence>;
  /**
   * « Créer un plan récurrent » depuis une fiche : ecrit d'abord son MODELE
   * (`modeleDepuisFiche`), puis le plan `suspendu` qui le cite
   * (`planDepuisFiche`). Deux temps : si le plan echoue, le modele reste,
   * visible en mode Modeles.
   */
  fromRecord(input: RecurrenceFromRecordInput): Promise<Recurrence>;
  /** « Créer un plan récurrent » depuis un modele existant (cf. `planDepuisModele`). */
  fromTemplate(input: RecurrenceFromTemplateInput): Promise<Recurrence>;
  /**
   * Demande un passage IMMEDIAT au serveur : ce plan seul, ou tous les plans
   * actifs de l'org sans `planId`. Le serveur refuse un lecteur seul.
   */
  lancer(planId?: string): Promise<ResultatLancement>;
}

/**
 * LIT UN PLAN BRUT et rend une `Recurrence` valide. Fonction PURE et exportee.
 * Un plan d'avant les modeles se lit aussi (`templateId` vide, cf.
 * `RecurrenceSchema`) : son ancien contenu est ignore.
 */
export function lireRecurrence(raw: DocumentData): Recurrence {
  return RecurrenceSchema.parse(raw);
}

const recurrenceConverter = createConverter<Recurrence>(lireRecurrence);

/**
 * @param getActorId - uid de l'utilisateur authentifie courant (les rules
 *   exigent createdBy/updatedBy == request.auth.uid).
 * @param app - l'application Firebase, pour la callable de `lancer`.
 * @param emulator - `config.emulators?.functions` en dev local, sinon omis.
 * @param modeles - rend le repository des modeles : un plan lit le sien
 *   avant d'ecrire, et `fromRecord` en cree un. Une fermeture, lue a l'appel :
 *   `createTakiSdk` construit les deux repositories dans le meme objet.
 */
export function createRecurrenceRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
  app: FirebaseApp,
  emulator: EmulatorEndpoint | undefined,
  modeles: () => TemplateRepository,
): RecurrenceRepository {
  const col = collection(db, RECURRENCES_COLLECTION).withConverter(recurrenceConverter);

  /**
   * LA SUPPRESSION D'UN PLAN LIBERE SON MODELE, dans le meme lot. Rien a faire
   * pour un plan d'avant les modeles (pas de `templateId`), pour un modele
   * disparu, ou qui ne le cite pas : les rules ne demandent alors rien au
   * modele, et une ecriture inutile y serait refusee (un modele supprime ne
   * s'ecrit plus).
   */
  const archiveOps = createArchiveOps<Recurrence>(db, col, RECURRENCES_COLLECTION, getActorId, {
    async avantSuppression(id: string, batch: WriteBatch, avant: DocumentData): Promise<void> {
      const templateId: unknown = avant['templateId'];
      if (typeof templateId !== 'string' || templateId === '') return;
      const ref = doc(db, TEMPLATES_COLLECTION, templateId);
      const modele = await getDoc(ref);
      if (!modele.exists()) return;
      const plans: unknown = modele.get('plansIds');
      if (!Array.isArray(plans) || !plans.includes(id)) return;
      batch.update(ref, {
        plansIds: arrayRemove(id),
        updatedAt: serverTimestamp(),
        updatedBy: getActorId(),
      });
    },
  });

  async function readOne(id: string): Promise<Recurrence | null> {
    const snap = await getDoc(doc(col, id));
    if (!snap.exists()) return null;
    return snap.data();
  }

  /** Lit le modele cite par un plan, et leve la raison s'il ne peut pas le porter. */
  async function exigerModele(
    plan: PayloadDePlan & { orgId: string; templateId: string },
  ): Promise<Template> {
    const modele = await modeles().get(plan.templateId);
    const raison = raisonDuModele(plan, modele);
    if (raison !== null || modele === null) throw new Error(raison ?? '');
    const verdict = validerPlanContreFamille(plan, modele, plan.orgId);
    if (!verdict.ok) throw new Error(verdict.raison);
    return modele;
  }

  async function create(input: RecurrenceCreateInput): Promise<Recurrence> {
    // Valider AVANT d'ecrire : la forme du plan (Zod), son modele (present,
    // actif, meme cible), puis la fiche qu'il produira (schema de la famille).
    const fields = RecurrenceCreateInputSchema.parse(input);
    await exigerModele(fields);

    const actor = getActorId();
    const ref = doc(col);
    const now = new Date();

    // LES CHAMPS MACHINE NE PARTENT PAS : les rules refusent une creation qui
    // en porte un. On ne recopie donc que les champs du plan, un par un ; les
    // facultatifs seulement s'ils sont fournis.
    const optional: DocumentData = {};
    for (const clef of CLEFS_EFFACABLES) {
      const valeur = (fields as Record<string, unknown>)[clef];
      if (valeur !== undefined) optional[clef] = valeur;
    }
    if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;
    if (fields.couleur != null) optional['couleur'] = fields.couleur;
    if (fields.photo != null) optional['photo'] = fields.photo;

    const payload: DocumentData = {
      orgId: fields.orgId,
      type: fields.type,
      cibleType: fields.cibleType,
      name: fields.name,
      status: fields.status,
      mode: fields.mode,
      rythmeUnite: fields.rythmeUnite,
      rythmeIntervalle: fields.rythmeIntervalle,
      debut: fields.debut,
      avanceJours: fields.avanceJours,
      sauts: fields.sauts,
      templateId: fields.templateId,
      ...optional,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: actor,
      updatedBy: actor,
      archivedAt: null,
      deletedAt: null,
      schemaVersion: CURRENT_SCHEMA_VERSION,
    };

    // UN SEUL LOT : le plan, et son id inscrit dans le modele. `arrayUnion` et
    // non le tableau entier : deux plans crees en meme temps sur un meme
    // modele ne s'effacent pas l'un l'autre (les rules le verifient par
    // `getAfter`, qui voit la transformation).
    const batch = writeBatch(db);
    batch.set(doc(db, RECURRENCES_COLLECTION, ref.id), payload);
    batch.update(doc(db, TEMPLATES_COLLECTION, fields.templateId), {
      plansIds: arrayUnion(ref.id),
      updatedAt: serverTimestamp(),
      updatedBy: actor,
    });
    await batch.commit();

    return lireRecurrence({ id: ref.id, ...payload, createdAt: now, updatedAt: now });
  }

  async function update(id: string, patch: RecurrenceUpdateInput): Promise<Recurrence> {
    // Les `null` des clefs facultatives deviennent des effacements, AVANT Zod
    // (cf. `CLEFS_EFFACABLES`) ; un `null` ailleurs reste a Zod, qui le refuse.
    const brut = patch as Record<string, unknown>;
    const effaces = Object.keys(brut).filter((clef) => brut[clef] === null && CLEFS_EFFACABLES.has(clef));
    const reste = Object.fromEntries(Object.entries(brut).filter(([clef]) => !effaces.includes(clef)));
    const fields = RecurrenceUpdateInputSchema.parse(reste);

    // LA CIBLE OU LES CHEMINS CHANGENT : on revalide le plan ENTIER tel qu'il
    // sera, contre son modele relu. Un patch qui retire le libelle d'un projet
    // ne se decouvre pas la nuit, il se refuse ici.
    if (CLEFS_DU_CONTENU.some((clef) => clef in brut)) {
      const actuel = await readOne(id);
      if (actuel === null) throw new Error(`Plan ${id} introuvable : rien n'a ete ecrit.`);
      const futur: Record<string, unknown> = { ...actuel, ...fields };
      for (const clef of effaces) delete futur[clef];
      if (actuel.templateId === '') {
        throw new Error(
          "Ce plan date d'avant les modèles : il ne se corrige plus, supprimez-le et recréez-le depuis une fiche.",
        );
      }
      await exigerModele(futur as unknown as PayloadDePlan & { orgId: string; templateId: string });
    }

    const data = buildUpdateData(fields);
    for (const clef of effaces) data[clef] = deleteField();
    data['updatedAt'] = serverTimestamp();
    data['updatedBy'] = getActorId();

    await updateDoc(doc(db, RECURRENCES_COLLECTION, id), data);

    const relu = await readOne(id);
    if (relu === null) throw new Error(`Plan ${id} introuvable apres mise a jour.`);
    return relu;
  }

  /** Le client de la callable, fabrique au PREMIER `lancer` : la plupart des ecrans n'en ont jamais besoin. */
  let appeler: ((payload: Record<string, unknown>) => Promise<{ data: unknown }>) | null = null;
  function callable(): (payload: Record<string, unknown>) => Promise<{ data: unknown }> {
    if (appeler !== null) return appeler;
    const functions = getFunctions(app, RECURRENCES_FUNCTIONS_REGION);
    if (emulator && !emulatorBoundFunctions.has(functions)) {
      connectFunctionsEmulator(functions, emulator.host, emulator.port);
      emulatorBoundFunctions.add(functions);
    }
    appeler = httpsCallable<unknown, unknown>(functions, CALLABLE_RECURRENCES);
    return appeler;
  }

  return {
    create,
    update,

    async list(options?: ListOptions): Promise<Recurrence[]> {
      // Ici `type` vaut la FAMILLE cible : `list({ type: 'projects' })` rend
      // les plans de projets, cote serveur (cf. l'en-tete de recurrence.ts).
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      return runCount(col, orgId, options);
    },

    async sum(field: string, options?: CountOptions): Promise<number> {
      return runSum(col, orgId, field, options);
    },

    async average(field: string, options?: CountOptions): Promise<number | null> {
      return runAverage(col, orgId, field, options);
    },

    async get(id: string): Promise<Recurrence | null> {
      return readOne(id);
    },

    async fromRecord(input: RecurrenceFromRecordInput): Promise<Recurrence> {
      // DEUX TEMPS, et c'est assume : le modele d'abord (il faut son id), puis
      // le lot du plan. Un echec du plan laisse un modele sans plan, visible et
      // retirable en mode Modeles ; l'inverse (un plan sans modele) n'existe pas.
      const modele = await modeles().create(modeleDepuisFiche(orgId, input));
      return create(planDepuisFiche(orgId, input, modele.id));
    },

    async fromTemplate(input: RecurrenceFromTemplateInput): Promise<Recurrence> {
      const modele = await modeles().get(input.templateId);
      if (modele === null) {
        throw new Error("Le modèle de ce plan est introuvable (supprimé) : rien n'a été écrit.");
      }
      const plan = planDepuisModele(orgId, modele, input.name);
      if (input.description !== undefined) plan.description = input.description;
      return create(plan);
    },

    async lancer(planId?: string): Promise<ResultatLancement> {
      try {
        const reponse = await callable()(planId === undefined ? {} : { planId });
        return lireResultatLancement(reponse.data);
      } catch (cause: unknown) {
        throw enErreur(cause);
      }
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
